import React from 'react'
import { createPortal } from 'react-dom'
import { useDialog } from '../../hooks/useDialog'
import { goalsDraft } from '../../plan/goalsDraft'
import type { GoalDraft } from '../../plan/planForm'
import '../../components/Sheet.css'
import '../settings/Settings.css'
import GoalFieldsForm, { useGoalFields } from './GoalFields'

interface Props {
  goal: GoalDraft
  saving: boolean
  error: string
  onSave: (goal: GoalDraft) => void
  onClose: () => void
}

// GoalSheet edits the saved goal on one card. Closing it drops the edit draft,
// so the next time it opens it starts from the saved goal.
export default function GoalSheet({ goal, saving, error, onSave, onClose }: Props) {
  const { fields, errors, set, submit } = useGoalFields(goal, 'edit')
  const dialogRef = React.useRef<HTMLDivElement>(null)

  const close = () => {
    goalsDraft.clearSetup()
    onClose()
  }
  useDialog(dialogRef, close, { closable: !saving })

  const save = (e: React.FormEvent) => {
    e.preventDefault()
    const next = submit()
    if (next) onSave(next)
  }

  const sheet = (
    <div className="sheet-overlay" onClick={() => { if (!saving) close() }}>
      <div ref={dialogRef} className="sheet-content" role="dialog" aria-modal="true" aria-label="Your goal" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle"></div>
        <div className="sheet-header">
          <h2>Your goal</h2>
        </div>
        <form className="settings-form sheet-form" onSubmit={save} noValidate>
          <div className="sheet-body">
            <GoalFieldsForm fields={fields} errors={errors} onChange={set} />
            {error && (
              <p className="field-error" role="alert">
                {error}
              </p>
            )}
          </div>
          <div className="sheet-actions">
            <button type="button" className="btn btn-secondary" onClick={close} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save goal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  const host = typeof document === 'undefined' ? null : document.querySelector('.app-container')
  return host ? createPortal(sheet, host) : sheet
}
